// The page's entry point: holds the game, takes the player's taps, animates each turn and saves the game.

import { EMPTY } from './game/board.ts';
import { move, newGame } from './game/game.ts';
import { NEW_GAME_HISTORY, RESUMED_HISTORY, afterMove, undo } from './game/undo.ts';
import { playTurn } from './ui/animate.ts';
import { load, store } from './ui/storage.ts';
import { createView, render } from './ui/view.ts';

const view = createView();
const saved = load();
let game = saved?.game ?? newGame(Math.random);
/** What Undo can bring back. */
let history = saved === null ? NEW_GAME_HISTORY : RESUMED_HISTORY;
/** The highest score of the games that ended before this one. */
let best = saved?.best ?? 0;
/** The cell of the ball the player picked, if any. */
let selected: number | null = null;
/** True while a turn is animated; taps are ignored meanwhile. */
let busy = false;

view.cells.forEach((cell, index) => cell.addEventListener('click', () => void tap(index)));
view.undo.addEventListener('click', undoMove);
for (const button of view.newGameButtons) button.addEventListener('click', requestNewGame);
view.confirm.addEventListener('close', () => {
  if (view.confirm.returnValue === 'new') startNewGame();
});

if (game.over) {
  // A finished game isn't resumed.
  startNewGame();
} else {
  store(game, best);
  redraw();
}

// Offline play, and newer files on every online load: see src/sw/sw.ts.
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.register('sw.js').catch((error) => console.warn('Offline play is unavailable.', error));
} else {
  console.warn('Offline play is unavailable: service workers need HTTPS or localhost.');
}

async function tap(cell: number): Promise<void> {
  if (busy || game.over) return;
  if (game.board[cell] !== EMPTY) {
    select(cell === selected ? null : cell);
    return;
  }
  if (selected === null) return;
  const turn = move(game, selected, cell, Math.random);
  // No free path: the ball stays selected.
  if (turn === null) return;
  // Saved before the animation, so closing the page mid-turn keeps the turn.
  store(turn.game, best);
  select(null);
  busy = true;
  try {
    await playTurn(view, turn, game.score);
  } finally {
    // Even if an animation fails, the screen ends up showing the actual game.
    history = afterMove(history, game);
    game = turn.game;
    redraw();
    busy = false;
  }
}

/** Takes back the last move, at once and without animation. */
function undoMove(): void {
  if (busy) return;
  const undone = undo(history);
  // The button is disabled when there is nothing to undo, so this is only a type guard.
  if (undone === null) return;
  game = undone.game;
  history = undone.history;
  select(null);
  store(game, best);
  redraw();
}

/** Starts a new game, asking first while the current one isn't over. */
function requestNewGame(): void {
  if (busy) return;
  if (game.over) {
    startNewGame();
    return;
  }
  // Escape closes the dialog without setting a value, so clear the previous answer first.
  view.confirm.returnValue = '';
  view.confirm.showModal();
}

/** Ends the current game, counting its score towards the best, and starts a new one. */
function startNewGame(): void {
  best = Math.max(best, game.score);
  select(null);
  game = newGame(Math.random);
  history = NEW_GAME_HISTORY;
  store(game, best);
  redraw();
}

/** Draws the game, with Undo enabled when there is a move to take back. */
function redraw(): void {
  render(view, game, best, history.previous !== null);
}

function select(cell: number | null): void {
  if (selected !== null) view.cells[selected].classList.remove('selected');
  selected = cell;
  if (cell !== null) view.cells[cell].classList.add('selected');
}
